/**
 * Wiener Zeit für Kalender-Anzeige und -Abfrage. (20.10.2026, Kalender-Überarbeitung)
 *
 * Unabhängig von der Zeitzone des Rechners: Tag und Uhrzeit eines Zeitpunkts gelten immer in Europe/Vienna,
 * sonst rutscht ein Termin um 23:30 beim Server (UTC) oder im Browser eines Reisenden auf den falschen Tag.
 * Tage sind Zeichenketten „YYYY-MM-DD“; ein Ganztag-Ende bei Google ist EXKLUSIV (der Folgetag).
 */
const WIEN = "Europe/Vienna";
const TEILE = new Intl.DateTimeFormat("en-GB", { timeZone: WIEN, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" });

export const TAG_FORM = /^\d{4}-\d{2}-\d{2}$/;
export const WOCHENTAGE_KURZ = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"] as const;
export const WOCHENTAGE_LANG = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"] as const;

function teile(d: Date): { y: number; mo: number; d: number; h: number; mi: number; s: number } {
  const p: Record<string, number> = {};
  for (const t of TEILE.formatToParts(d)) if (t.type !== "literal") p[t.type] = Number(t.value);
  return { y: p.year!, mo: p.month!, d: p.day!, h: p.hour!, mi: p.minute!, s: p.second! };
}

const zwei = (n: number): string => String(n).padStart(2, "0");

function zerlegt(tag: string): [number, number, number] {
  const [y, m, d] = tag.split("-").map(Number) as [number, number, number];
  return [y, m, d];
}

/** Der Tag eines Zeitpunkts in Wiener Zeit. */
export function wienTag(d: Date): string {
  const t = teile(d);
  return `${t.y}-${zwei(t.mo)}-${zwei(t.d)}`;
}

/** „09:05“ in Wiener Zeit. */
export function wienUhr(d: Date): string {
  const t = teile(d);
  return `${zwei(t.h)}:${zwei(t.mi)}`;
}

/** Der Zeitpunkt, an dem es in Wien an diesem Tag 00:00 ist (Sommer-/Winterzeit eingerechnet). */
export function wienTagBeginn(tag: string): Date {
  const [y, m, d] = zerlegt(tag);
  const ziel = Date.UTC(y, m - 1, d);
  let t = ziel;
  for (let i = 0; i < 3; i++) {
    const w = teile(new Date(t));
    t -= Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s) - ziel;
  }
  return new Date(t);
}

/** Tag plus n Tage (Kalenderrechnung, nicht 24-Stunden-Rechnung). */
export function tagPlus(tag: string, n: number): string {
  const [y, m, d] = zerlegt(tag);
  const r = new Date(Date.UTC(y, m - 1, d + n));
  return `${r.getUTCFullYear()}-${zwei(r.getUTCMonth() + 1)}-${zwei(r.getUTCDate())}`;
}

/** Wochentag 0 (So) bis 6 (Sa) eines Tages. */
export function wochentag(tag: string): number {
  const [y, m, d] = zerlegt(tag);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** „20.10.“ bzw. „20.10.2027“, wenn nicht im Jahr von `jetzt`. */
export function datumKurz(tag: string, jetzt: Date): string {
  const [y, m, d] = zerlegt(tag);
  const jahr = Number(wienTag(jetzt).slice(0, 4));
  return `${zwei(d)}.${zwei(m)}.${y === jahr ? "" : y}`;
}
